import React, { useState, useEffect, useMemo, useRef } from 'react';
import Pagination from '../../components/Pagination.tsx';
import styles from '../../styles/GeneracionCargosManualesStyles/GeneracionCargosManuales.module.css';

function StudentTable({
  students = [],
  onSelectStudent,
  serverPaging = false,
  page: serverPage = 1,
  totalPages: serverTotalPages = 1,
  onPageChange: onServerPageChange,
  totalCount = null,
  actions = [],
  onAction,
}) {
  // Paginación similar a MateriasTable (cliente) o servidor según props
  const [page, setPage] = useState(1);
  const pageSize = 8;
  const totalPages = serverPaging ? Math.max(1, serverTotalPages) : Math.max(1, Math.ceil(students.length / pageSize));
  const paginated = serverPaging ? students : students.slice((page - 1) * pageSize, page * pageSize);

  // Crear clave única basada en IDs para detectar cambios reales
  const studentsKey = useMemo(() => {
    return students.map(s => s.id_estudiante || s.id).sort().join(',');
  }, [students]);

  const prevStudentsKeyRef = useRef(studentsKey);
  const [menuState, setMenuState] = useState({ student: null, top: 0, left: 0 });
  const actionsHostRef = useRef(null);
  const menuRef = useRef(null);
  const hasActions = Array.isArray(actions) && actions.length > 0;

  // Reiniciar página solo cuando realmente cambia la lista de estudiantes (cliente)
  useEffect(() => {
    if (prevStudentsKeyRef.current !== studentsKey) {
      setPage(1);
      prevStudentsKeyRef.current = studentsKey;
    }
  }, [studentsKey]);

  // Ajustar página si quedó fuera de rango (cliente)
  useEffect(() => {
    if (!serverPaging && page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages, serverPaging]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickInsideMenu = menuRef.current && menuRef.current.contains(event.target);
      const clickInsideHost = actionsHostRef.current && actionsHostRef.current.contains(event.target);
      if (!clickInsideMenu && !clickInsideHost) {
        setMenuState({ student: null, top: 0, left: 0 });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const closeMenu = () => setMenuState({ student: null, top: 0, left: 0 });
    window.addEventListener('resize', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    return () => {
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, []);

  const handleActionClick = (action, student) => {
    setMenuState({ student: null, top: 0, left: 0 });
    if (typeof onAction === 'function') {
      onAction(action, student);
    }
  };

  const handleOpenActionsMenu = (event, student) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 220;
    const preferredLeft = rect.right - menuWidth;
    const left = Math.max(8, Math.min(preferredLeft, window.innerWidth - menuWidth - 8));
    const top = Math.max(8, rect.bottom + 6);
    const currentId = menuState.student?.id || menuState.student?.id_estudiante;
    const nextId = student.id || student.id_estudiante;

    if (currentId === nextId) {
      setMenuState({ student: null, top: 0, left: 0 });
      return;
    }

    setMenuState({ student, top, left });
  };

  return (
    <div className={styles.tableSection} ref={actionsHostRef}>
      <div className={styles.tableHeader}>
        <div className={styles.tableIcon}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 21V19C6 17.9391 6.42143 16.9217 7.17157 16.1716C7.92172 15.4214 8.93913 15 10 15H14C15.0609 15 16.0783 15.4214 16.8284 16.1716C17.5786 16.9217 18 17.9391 18 19V21" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h3 className={styles.tableTitle}>Estudiantes</h3>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Matrícula</th>
              <th>Campus</th>
              <th>Sección</th>
              <th>Programa</th>
              <th>Ciclo Escolar</th>
              {hasActions && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {paginated.length > 0 ? (
              paginated.map((student, index) => (
                <tr 
                  key={student.id || student.id_estudiante}
                  className={index % 2 === 0 ? styles.evenRow : styles.oddRow}
                  onClick={() => onSelectStudent(student)}
                >
                  <td className={styles.nameCell}>{student.nombre}</td>
                  <td>{student.matricula}</td>
                  <td>{student.campus}</td>
                  <td>{student.seccion}</td>
                  <td>{student.programa}</td>
                  <td>{student.ciclo || student.cicloEscolar || 'N/A'}</td>
                  {hasActions && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          type="button"
                          aria-label="Abrir acciones"
                          onClick={(e) => handleOpenActionsMenu(e, student)}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: 6,
                            background: '#fff',
                            width: 32,
                            height: 32,
                            cursor: 'pointer',
                            fontSize: 18,
                            lineHeight: 1,
                            color: '#374151'
                          }}
                        >
                          &#8942;
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={hasActions ? 7 : 6} style={{ textAlign: 'center', padding: '24px 12px' }} className={styles.noResults}>
                  No se encontraron estudiantes con los criterios de búsqueda.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Paginación al pie de la tabla */}
      {totalPages > 1 && (
        <Pagination
          page={serverPaging ? serverPage : page}
          totalPages={totalPages}
          onPageChange={serverPaging ? onServerPageChange : setPage}
        />
      )}
      {typeof totalCount === 'number' && (
        <div style={{ padding: '10px 16px 14px', borderTop: '1px solid #f0f0f0', color: '#6b7280', fontSize: 13 }}>
          Total de estudiantes: {totalCount}
        </div>
      )}
      {hasActions && menuState.student && (
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            left: menuState.left,
            top: menuState.top,
            width: 220,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.14)',
            zIndex: 2000,
            overflow: 'hidden'
          }}
        >
          {actions.map((action, idx) => (
            <button
              key={action.key}
              type="button"
              onClick={() => handleActionClick(action, menuState.student)}
              style={{
                width: '100%',
                textAlign: 'left',
                border: 'none',
                borderBottom: idx === actions.length - 1 ? 'none' : '1px solid #f3f4f6',
                background: '#fff',
                padding: '10px 12px',
                cursor: 'pointer',
                color: '#111827',
                fontSize: 13
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default StudentTable;
